import { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import axios from 'axios';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { Box, IconButton, Tooltip, HStack } from '@chakra-ui/react';
import NumberFormatCustom from 'components/maintenance/NumberFormatCustom';
import { useNavigate } from 'react-router-dom';
import path from '@/routes/constants';
import { Button } from 'components/v4';
import { BsThreeDotsVertical } from 'react-icons/bs';
import CallingRatesForm from './CallingRatesForm';

/**
 * Component to render the Calling Tab of the Plans component
 */
const Calling = ({ child }) => {
    const handleSave = async (values) => {
        // Remove keys with empty string, null, or undefined values
        const filteredValues = Object.fromEntries(
            Object.entries(values).filter(
                ([, v]) => v !== '' && v !== null && v !== undefined,
            ),
        );
        setIsSaving(true);
        try {
            if (action === 'ADD') {
                await axios.post('/rate', {
                    ...filteredValues,
                    isActive: true,
                    planID: child?.id || null,
                });
            } else {
                await axios.put('/rate', {
                    ...filteredValues,
                    id: values.id,
                });
            }
            setAction('GET');
            setEditData(null);
            refetch();
        } catch (err) {
            // Optionally handle error
        } finally {
            setIsSaving(false);
        }
    };
    const tableRef = useRef();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // const { child } = useSelector((state) => ({ ...state.table }));
    const [action, setAction] = useState('GET'); // GET | EDIT | ADD
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Handlers
    const handleEdit = (row) => {
        setAction('EDIT');
        setEditData(row);
    };

    const handleDelete = async (row) => {
        if (!row?.id) return;
        setIsDeleting(true);
        try {
            await axios.put('/rate', {
                ...row,
                id: row.id,
                isActive: false,
            });
            refetch();
            setAction('GET');
            setEditData(null);
        } catch (err) {
            console.error('Failed to delete calling rate:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancel = () => {
        setAction('GET');
        setEditData(null);
    };

    const handleCreate = () => {
        setAction('ADD');
        setEditData(null);
    };

    // Fetch rates data using TanStack Query
    const {
        data: ratesData = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['callingRates', child?.id],
        queryFn: () => fetchRates(child?.id),
        enabled: !!child?.id,
    });
    const { darkMode } = useSelector((state) => state.settings);
    // AGGrid columns
    const columns = [
        { headerName: 'Name', field: 'name', flex: 1 },
        {
            headerName: 'Priority',
            field: 'priority',
            flex: 1,
            sort: 'asc',
            sortIndex: 0,
        },
        { headerName: 'Prefix', field: 'prefix', flex: 1 },
        {
            headerName: 'Flag Fall In Rate',
            field: 'flagFallInRate',
            flex: 1,
            cellRenderer: (params) => (
                <NumberFormatCustom displayType="text" value={params.value} />
            ),
        },
        {
            headerName: 'Flag Fall Out Rate',
            field: 'flagFallOutRate',
            flex: 1,
            cellRenderer: (params) => (
                <NumberFormatCustom displayType="text" value={params.value} />
            ),
        },
        {
            headerName: 'Inbound Rate',
            field: 'inboundRate',
            flex: 1,
            cellRenderer: (params) => (
                <NumberFormatCustom displayType="text" value={params.value} />
            ),
        },
        {
            headerName: 'Outbound Rate',
            field: 'outboundRate',
            flex: 1,
            cellRenderer: (params) => (
                <NumberFormatCustom displayType="text" value={params.value} />
            ),
        },
        {
            headerName: 'Action',
            lockPosition: 'right',
            width: 120,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            cellRenderer: (params) => (
                <Tooltip label="Edit">
                    <IconButton
                        icon={
                            <BsThreeDotsVertical
                                fill={darkMode ? 'white' : 'black'}
                            />
                        }
                        aria-label="Edit"
                        variant="ghost"
                        onClick={() => handleEdit(params.data)}
                        _hover={{
                            bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                        }}
                        _active={{
                            bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                        }}
                        _focus={{ boxShadow: 'none' }}
                    />
                </Tooltip>
            ),
        },
    ];

    // Render logic
    if (action !== 'GET') {
        const defaultInitialValues = {
            name: '',
            priority: '',
            prefix: '',
            flagFallInRate: '',
            flagFallOutRate: '',
            inboundRate: '',
            outboundRate: '',
        };
        return (
            <CallingRatesForm
                mode={action.toLowerCase()}
                initialValues={editData || defaultInitialValues}
                isLoading={isSaving}
                isDeleting={isDeleting}
                onBack={handleCancel}
                onSubmit={handleSave}
                onDelete={() => handleDelete(editData)}
                darkMode={darkMode}
            />
        );
    }
    return (
        <Box style={{ minHeight: '400px', overflowY: 'auto' }}>
            <AGGridMain
                gridRef={tableRef}
                tableData={{ columns, data: ratesData }}
                refetch={refetch}
                isLoading={isLoading}
                titleName="Calling Rates"
                extraComponents={
                    <HStack spacing={2} paddingLeft={5}>
                        <Tooltip
                            label="CSV Update"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}>
                                <Button
                                    size="sm"
                                    width="110px"
                                    onClick={() => {
                                        const urlPathname = `/plans/${path.CSVCALLINGRATES}?plan=${encodeURIComponent(
                                            child?.name?.replace(/ /g, '_'),
                                        )}&planID=${encodeURIComponent(child?.id)}`;
                                        dispatch({
                                            type: 'UPDATE_HISTORY',
                                            payload: {
                                                pathname: urlPathname,
                                            },
                                        });
                                        navigate(urlPathname?.split('&')[0]);
                                    }}>
                                    CSV Update
                                </Button>
                            </div>
                        </Tooltip>
                        <Tooltip
                            label="Calling Rates"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}>
                                <Button
                                    size="sm"
                                    width="110px"
                                    onClick={handleCreate}>
                                    + Calling Rates
                                </Button>
                            </div>
                        </Tooltip>
                    </HStack>
                }
            />

            {error && <Box color="red.500">Error loading rates data.</Box>}
        </Box>
    );
};

const fetchRates = async (planId) => {
    if (!planId) return [];
    const response = await axios.get(`/rates/${planId}`);
    return response.data;
};

export default Calling;
